import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { compareRanks } from '../components/Layout/LayoutBeta/dnd/rank';

export type NodeKind =
  | 'folder'
  | 'agent'
  | 'form'
  | 'fluxbot'
  | 'object'
  | 'vector';

export type Section = 'agents' | 'data';

export interface SidebarNode {
  id: string;
  parentId: string | null;
  kind: NodeKind;
  section: Section;
  spaceId: string | null;
  rank: string;
  localVersion: number;
  name: string;
  payload: unknown;
}

export type RootBySectionKey = `${Section}:${string}`;

const rootKey = (section: Section, spaceId: string | null): RootBySectionKey =>
  `${section}:${spaceId ?? 'private'}`;

export interface ApplyMovePatch {
  parentId: string | null;
  rank: string;
  section?: Section;
  spaceId?: string | null;
}

export interface NodeSnapshot {
  node: SidebarNode;
}

interface SidebarTreeState {
  nodes: Record<string, SidebarNode>;
  childrenByParent: Record<string, string[]>;
  rootBySection: Record<RootBySectionKey, string[]>;
  pendingMoves: Set<string>;
  hydrate: (nodes: SidebarNode[]) => void;
  upsertNode: (node: SidebarNode) => void;
  removeNode: (id: string) => void;
  applyMove: (id: string, patch: ApplyMovePatch) => number | null;
  patchNode: (node: SidebarNode, version: number) => void;
  revertMove: (id: string, snapshot: NodeSnapshot, version: number) => void;
  snapshot: (id: string) => NodeSnapshot | null;
  markPending: (id: string) => void;
  clearPending: (id: string) => void;
}

const insertSorted = (
  list: string[],
  id: string,
  nodes: Record<string, SidebarNode>,
): string[] => {
  const next = list.filter(x => x !== id);
  const inserted = nodes[id];
  if (!inserted) return next;
  let i = 0;
  while (i < next.length) {
    const other = nodes[next[i]];
    if (!other) {
      i++;
      continue;
    }
    if (compareRanks(inserted.rank, other.rank) < 0) break;
    i++;
  }
  next.splice(i, 0, id);
  return next;
};

const removeFromBucket = (list: string[] | undefined, id: string): string[] => {
  if (!list) return [];
  return list.filter(x => x !== id);
};

const placeNode = (
  state: SidebarTreeState,
  node: SidebarNode,
): {
  childrenByParent: Record<string, string[]>;
  rootBySection: Record<RootBySectionKey, string[]>;
} => {
  const childrenByParent = { ...state.childrenByParent };
  const rootBySection = { ...state.rootBySection };

  if (node.parentId === null) {
    const key = rootKey(node.section, node.spaceId);
    rootBySection[key] = insertSorted(
      rootBySection[key] ?? [],
      node.id,
      state.nodes,
    );
  } else {
    childrenByParent[node.parentId] = insertSorted(
      childrenByParent[node.parentId] ?? [],
      node.id,
      state.nodes,
    );
  }
  return { childrenByParent, rootBySection };
};

const unplaceNode = (
  state: SidebarTreeState,
  node: SidebarNode,
): {
  childrenByParent: Record<string, string[]>;
  rootBySection: Record<RootBySectionKey, string[]>;
} => {
  const childrenByParent = { ...state.childrenByParent };
  const rootBySection = { ...state.rootBySection };
  if (node.parentId === null) {
    const key = rootKey(node.section, node.spaceId);
    rootBySection[key] = removeFromBucket(rootBySection[key], node.id);
  } else {
    childrenByParent[node.parentId] = removeFromBucket(
      childrenByParent[node.parentId],
      node.id,
    );
  }
  return { childrenByParent, rootBySection };
};

export const useSidebarTreeStore = create<SidebarTreeState>()(
  subscribeWithSelector((set, get) => ({
    nodes: {},
    childrenByParent: {},
    rootBySection: {},
    pendingMoves: new Set<string>(),

    hydrate: nodes => {
      set(state => {
        const nextNodes: Record<string, SidebarNode> = {};
        for (const incoming of nodes) {
          if (state.pendingMoves.has(incoming.id)) {
            const existing = state.nodes[incoming.id];
            if (existing) {
              nextNodes[incoming.id] = existing;
              continue;
            }
          }
          const existing = state.nodes[incoming.id];
          nextNodes[incoming.id] = {
            ...incoming,
            localVersion: existing?.localVersion ?? 0,
          };
        }
        const childrenByParent: Record<string, string[]> = {};
        const rootBySection: Record<RootBySectionKey, string[]> = {};
        const ids = Object.keys(nextNodes);
        ids.sort((a, b) => compareRanks(nextNodes[a].rank, nextNodes[b].rank));
        for (const id of ids) {
          const node = nextNodes[id];
          if (node.parentId === null) {
            const key = rootKey(node.section, node.spaceId);
            (rootBySection[key] = rootBySection[key] ?? []).push(id);
          } else {
            (childrenByParent[node.parentId] =
              childrenByParent[node.parentId] ?? []).push(id);
          }
        }
        return {
          nodes: nextNodes,
          childrenByParent,
          rootBySection,
        };
      });
    },

    upsertNode: node => {
      set(state => {
        const existing = state.nodes[node.id];
        const cleaned = existing ? unplaceNode(state, existing) : null;
        const nextNodes = { ...state.nodes, [node.id]: node };
        const intermediate: SidebarTreeState = {
          ...state,
          nodes: nextNodes,
          childrenByParent: cleaned?.childrenByParent ?? state.childrenByParent,
          rootBySection: cleaned?.rootBySection ?? state.rootBySection,
        };
        const placed = placeNode(intermediate, node);
        return {
          nodes: nextNodes,
          childrenByParent: placed.childrenByParent,
          rootBySection: placed.rootBySection,
        };
      });
    },

    removeNode: id => {
      set(state => {
        const node = state.nodes[id];
        if (!node) return state;
        const cleaned = unplaceNode(state, node);
        const nextNodes = { ...state.nodes };
        delete nextNodes[id];
        return {
          nodes: nextNodes,
          childrenByParent: cleaned.childrenByParent,
          rootBySection: cleaned.rootBySection,
        };
      });
    },

    applyMove: (id, patch) => {
      const state = get();
      const existing = state.nodes[id];
      if (!existing) return null;
      const nextVersion = existing.localVersion + 1;
      const updated: SidebarNode = {
        ...existing,
        parentId: patch.parentId,
        rank: patch.rank,
        section: patch.section ?? existing.section,
        spaceId: patch.spaceId !== undefined ? patch.spaceId : existing.spaceId,
        localVersion: nextVersion,
      };
      set(s => {
        const cleaned = unplaceNode(s, existing);
        const nextNodes = { ...s.nodes, [id]: updated };
        const intermediate: SidebarTreeState = {
          ...s,
          nodes: nextNodes,
          childrenByParent: cleaned.childrenByParent,
          rootBySection: cleaned.rootBySection,
        };
        const placed = placeNode(intermediate, updated);
        const nextPending = new Set(s.pendingMoves);
        nextPending.add(id);
        return {
          nodes: nextNodes,
          childrenByParent: placed.childrenByParent,
          rootBySection: placed.rootBySection,
          pendingMoves: nextPending,
        };
      });
      return nextVersion;
    },

    patchNode: (node, version) => {
      const state = get();
      const existing = state.nodes[node.id];
      if (!existing) return;
      if (existing.localVersion !== version) return;
      const merged: SidebarNode = {
        ...existing,
        parentId: node.parentId,
        rank: node.rank,
        section: node.section,
        spaceId: node.spaceId,
        name: node.name,
        payload: node.payload,
      };
      set(s => {
        const cleaned = unplaceNode(s, existing);
        const nextNodes = { ...s.nodes, [node.id]: merged };
        const intermediate: SidebarTreeState = {
          ...s,
          nodes: nextNodes,
          childrenByParent: cleaned.childrenByParent,
          rootBySection: cleaned.rootBySection,
        };
        const placed = placeNode(intermediate, merged);
        const nextPending = new Set(s.pendingMoves);
        nextPending.delete(node.id);
        return {
          nodes: nextNodes,
          childrenByParent: placed.childrenByParent,
          rootBySection: placed.rootBySection,
          pendingMoves: nextPending,
        };
      });
    },

    revertMove: (id, snapshot, version) => {
      const state = get();
      const existing = state.nodes[id];
      if (!existing) return;
      if (existing.localVersion !== version) return;
      set(s => {
        const cleaned = unplaceNode(s, existing);
        const nextNodes = { ...s.nodes, [id]: snapshot.node };
        const intermediate: SidebarTreeState = {
          ...s,
          nodes: nextNodes,
          childrenByParent: cleaned.childrenByParent,
          rootBySection: cleaned.rootBySection,
        };
        const placed = placeNode(intermediate, snapshot.node);
        const nextPending = new Set(s.pendingMoves);
        nextPending.delete(id);
        return {
          nodes: nextNodes,
          childrenByParent: placed.childrenByParent,
          rootBySection: placed.rootBySection,
          pendingMoves: nextPending,
        };
      });
    },

    snapshot: id => {
      const node = get().nodes[id];
      if (!node) return null;
      return { node: { ...node } };
    },

    markPending: id => {
      set(s => {
        const next = new Set(s.pendingMoves);
        next.add(id);
        return { pendingMoves: next };
      });
    },

    clearPending: id => {
      set(s => {
        const next = new Set(s.pendingMoves);
        next.delete(id);
        return { pendingMoves: next };
      });
    },
  })),
);

export const rootKeyOf = rootKey;
